/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  TextField,
  FormControlLabel,
  Typography,
  Box,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";

const DomainApproverTargetEcuTypePopup = ({
  open,
  onClose,
  vehiclesData = [],
  ecuTypeData = [],
  exclusions = [],
  inclusions = [],
  onSave,
  reviewStatus = [],
  readOnly = false,
}) => {
  const [search, setSearch] = useState("");
  const [selectedMakeEcuPairs, setSelectedMakeEcuPairs] = useState([]);
  const [activeMake, setActiveMake] = useState(null);

  const availableMakes = useMemo(() => {
    if (
      vehiclesData &&
      Array.isArray(vehiclesData) &&
      vehiclesData.length > 0
    ) {
      const makes = vehiclesData.map((vehicle) => {
        const makeName = vehicle.make || vehicle.name;
        return makeName;
      });

      const uniqueMakes = [...new Set(makes)].filter(Boolean).sort((a, b) => a.localeCompare(b));
      return uniqueMakes;
    }

    if (ecuTypeData && Array.isArray(ecuTypeData) && ecuTypeData.length > 0) {
      const makes = ecuTypeData.map((item) => item.make).filter(Boolean);
      const uniqueMakes = [...new Set(makes)].sort((a, b) => a.localeCompare(b));
      return uniqueMakes;
    }

    return [];
  }, [ecuTypeData, vehiclesData]);

  const filteredEcuTypeData = useMemo(() => {
    if (!Array.isArray(ecuTypeData) || ecuTypeData.length === 0) {
      return [];
    }

    if (Array.isArray(availableMakes) && availableMakes.length > 0) {
      return ecuTypeData.filter((item) =>
        availableMakes.includes(item.make)
      );
    }

    return ecuTypeData;
  }, [ecuTypeData, availableMakes]);

  const getFilteredECUTypes = useMemo(() => {
    if (!filteredEcuTypeData || !activeMake) {
      return [];
    }

    const matchingEcuItem = filteredEcuTypeData.find(
      (item) => item.make === activeMake
    );

    if (matchingEcuItem?.ecuTypes) {
      return matchingEcuItem.ecuTypes.sort();
    }

    return [];
  }, [filteredEcuTypeData, activeMake]);

  const handleMakeChange = (make) => {
    if (activeMake === make) {
      return;
    } else {
      setActiveMake(make);
    }
  };

  const handleECUChange = (ecu) => {
    if (!activeMake) return;

    const pairExists = selectedMakeEcuPairs.some(
      (pair) => pair.make === activeMake && pair.ecu === ecu
    );

    if (pairExists) {
      setSelectedMakeEcuPairs((prev) =>
        prev.filter((pair) => !(pair.make === activeMake && pair.ecu === ecu))
      );
    } else {
      setSelectedMakeEcuPairs((prev) => [...prev, { make: activeMake, ecu }]);
    }
  };

  const makeHasSelectedEcus = (make) => {
    return selectedMakeEcuPairs.some((pair) => pair.make === make);
  };

  const isEcuSelected = (ecu) => {
    return selectedMakeEcuPairs.some(
      (pair) => pair.make === activeMake && pair.ecu === ecu
    );
  };

  const handleAllEcuChange = () => {
    if (!activeMake) return;

    const activeMakeEcus = getFilteredECUTypes;
    const selectedEcusForActiveMake = new Set(
      selectedMakeEcuPairs
        .filter((pair) => pair.make === activeMake)
        .map((pair) => pair.ecu)
    );

    const allSelected = activeMakeEcus.every((ecu) =>
      selectedEcusForActiveMake.has(ecu)
    );

    if (allSelected) {
      setSelectedMakeEcuPairs((prev) =>
        prev.filter((pair) => pair.make !== activeMake)
      );
    } else {
      setSelectedMakeEcuPairs((prev) => {
        const filtered = prev.filter((pair) => pair.make !== activeMake);
        const newPairs = activeMakeEcus.map((ecu) => ({
          make: activeMake,
          ecu,
        }));
        return [...filtered, ...newPairs];
      });
    }
  };

  const handleClearAll = () => {
    setSelectedMakeEcuPairs([]);
    setActiveMake(null);
  };

  const handleSave = () => {
    const hasAnyEcuTypes = Array.isArray(filteredEcuTypeData) &&
      filteredEcuTypeData.some(
        (item) => Array.isArray(item.ecuTypes) && item.ecuTypes.length > 0
      );

    if (!hasAnyEcuTypes && Array.isArray(inclusions) && inclusions.includes("ALL_ALL")) {
      onSave("ecuTypeInclusions", ["ALL_ALL"]);
      onClose();
      return;
    }

    const makeGroups = {};

    selectedMakeEcuPairs.forEach((pair) => {
      if (!makeGroups[pair.make]) {
        makeGroups[pair.make] = [];
      }
      makeGroups[pair.make].push(pair.ecu);
    });

    const ecuInclusions = [];

    Object.entries(makeGroups).forEach(([make, ecus]) => {
      const makeEcuData = filteredEcuTypeData.find((item) => item.make === make);

      if (makeEcuData?.ecuTypes) {
        if (ecus.length === makeEcuData.ecuTypes.length) {
          ecuInclusions.push(`${make}_ALL`);
        } else {
          ecus.forEach((ecu) => {
            ecuInclusions.push(`${make}_${ecu}`);
          });
        }
      }
    });

    onSave("ecuTypeInclusions", ecuInclusions);
    onClose();
  };

  const isRestricted =
    readOnly ||
    reviewStatus.includes("FORCE_UNINSTALL") ||
    reviewStatus.includes("DELIST") ||
    reviewStatus.includes("SAVING");

  useEffect(() => {
    if (open && Array.isArray(inclusions) && inclusions.length > 0) {
      const initialPairs = [];

      // Check if ALL_ALL is present
      if (inclusions.includes("ALL_ALL")) {
        // Select all makes and all their ECUs
        ecuTypeData.forEach((makeData) => {
          if (makeData.make && Array.isArray(makeData.ecuTypes)) {
            makeData.ecuTypes.forEach((ecuType) => {
              initialPairs.push({ make: makeData.make, ecu: ecuType });
            });
          }
        });
      } else {
        inclusions.forEach((item) => {
          const [incMake, incEcuType] = item.split("_");

          if (incEcuType === "ALL") {
            const makeEcuData = ecuTypeData.find(
              (data) => data.make === incMake
            );
            if (makeEcuData && Array.isArray(makeEcuData.ecuTypes)) {
              makeEcuData.ecuTypes.forEach((ecuType) => {
                initialPairs.push({ make: incMake, ecu: ecuType });
              });
            }
          } else {
            initialPairs.push({ make: incMake, ecu: incEcuType });
          }
        });
      }

      setSelectedMakeEcuPairs(initialPairs);
    } else if (open) {
      setSelectedMakeEcuPairs([]);
      setActiveMake(null);
    }
  }, [open, inclusions, filteredEcuTypeData]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: "585px",
            height: "412px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          padding: "5px 24px",
        }}
      >
        Targeting - ECU Type
        <IconButton onClick={onClose}>
          <ClearIcon sx={{ color: "#2A2C2D" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: "#fff",
          padding: "0px 24px 0px 24px",
          display: "flex",
          flexDirection: "column",
          gap: "15px",
        }}
      >
        <TextField
          fullWidth
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          margin="dense"
          sx={{ marginTop: "0px", marginBottom: "0px" }}
          variant="outlined"
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />

        <Box
          display="flex"
          sx={{
            border: "1px solid transparent",
            gap: "18px",
            backgroundColor: "#F3F3F3",
            borderRadius: "8px",
            padding: "10px 12px 16px 12px",
            height: "220px",
          }}
        >
          <Box flex={1}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Make</strong>
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                maxHeight: "167px",
                overflowY: "auto",
                pr: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {availableMakes
                .filter((make) => {
                  const makeString = String(make);
                  return makeString
                    .toLowerCase()
                    .includes(search.toLowerCase());
                })
                .map((make) => {
                  const makeString = String(make);
                  const isActive = activeMake === makeString;
                  const hasSelections = makeHasSelectedEcus(makeString);
                  const labelColor = isActive || hasSelections
                    ? "#1976d2"
                    : "inherit";

                  return (
                    <FormControlLabel
                      sx={{
                        marginLeft: "0px !important",
                        backgroundColor: isActive
                          ? "rgba(25, 118, 210, 0.1)"
                          : "transparent",
                        borderRadius: "4px",
                        padding: "2px 8px",
                        margin: "1px 0",
                      }}
                      key={makeString}
                      control={
                        <Checkbox
                          checked={true} // Always show as checked for original styling
                          onChange={() => handleMakeChange(makeString)}
                          size="small"
                          sx={{
                            "&.Mui-checked": {
                              color: "#9e9e9e !important", // Always grey like original
                            },
                            "&:hover": {
                              backgroundColor: "rgba(25, 118, 210, 0.04)",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: hasSelections ? "bold" : "normal",
                            color: labelColor,
                          }}
                        >
                          {makeString}
                        </Typography>
                      }
                    />
                  );
                })}
            </Box>
          </Box>

          <Box flex={1}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>ECU Types</strong>
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                maxHeight: "190px",
                overflowY: "auto",
                pr: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {!activeMake && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    fontStyle: "italic",
                    textAlign: "center",
                    mt: 4,
                  }}
                >
                  Select a "Make" to view ECU Types
                </Typography>
              )}
              {activeMake && getFilteredECUTypes.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    fontStyle: "italic",
                    textAlign: "center",
                    mt: 4,
                  }}
                >
                  No ECU types available for {activeMake}
                </Typography>
              )}
              {activeMake && getFilteredECUTypes.length > 0 && (
                <>
                  {/* ALL option - only show when ECU types are available */}
                  <FormControlLabel
                    key="ALL"
                    control={
                      <Checkbox
                        checked={getFilteredECUTypes.every((ecu) =>
                          isEcuSelected(ecu)
                        )}
                        indeterminate={
                          getFilteredECUTypes.some((ecu) =>
                            isEcuSelected(ecu)
                          ) &&
                          !getFilteredECUTypes.every((ecu) =>
                            isEcuSelected(ecu)
                          )
                        }
                        onChange={handleAllEcuChange}
                        disabled={isRestricted}
                        size="small"
                        sx={{
                          "&.Mui-checked": { color: "#1976d2 !important" },
                          "&.MuiCheckbox-indeterminate": {
                            color: "#1976d2 !important",
                          },
                          "&:not(.Mui-checked)": {
                            color: "rgba(0, 0, 0, 0.54) !important",
                          },
                          "&.Mui-disabled": {
                            color: "rgba(0, 0, 0, 0.26) !important",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(25, 118, 210, 0.04)",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        ALL
                      </Typography>
                    }
                  />

                  {/* Individual ECU types - all shown as checked and disabled */}
                  {getFilteredECUTypes
                    .filter((ecu) => {
                      return ecu.toLowerCase().includes(search.toLowerCase());
                    })
                    .map((ecu) => (
                      <FormControlLabel
                        key={ecu}
                        control={
                          <Checkbox
                            checked={isEcuSelected(ecu)}
                            onChange={() => handleECUChange(ecu)}
                            disabled={isRestricted}
                            size="small"
                            sx={{
                              "&.Mui-checked": { color: "#1976d2 !important" },
                              "&:not(.Mui-checked)": {
                                color: "rgba(0, 0, 0, 0.54) !important",
                              },
                              "&.Mui-disabled": {
                                color: "rgba(0, 0, 0, 0.26) !important",
                              },
                              "&:hover": {
                                backgroundColor: "rgba(25, 118, 210, 0.04)",
                              },
                            }}
                          />
                        }
                        label={<Typography variant="body2">{ecu}</Typography>}
                      />
                    ))}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, paddingTop: "15px", paddingBottom: "12px" }}
      >
        <Button
          onClick={handleClearAll} // Always allow clear all in view mode
          sx={{
            textTransform: "none",
            fontWeight: 400,
            mr: "auto",
            color: "#5A5B5C",
            opacity: 1, // Always visible
            pointerEvents: "auto", // Always clickable
          }}
          disabled={isRestricted}
        >
          Clear All
        </Button>

        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 400,
            width: "114px",
            height: "36px",
            borderRadius: "4px",
            backgroundColor: "#1214151A",
            color: "#2A2C2D",
          }}
        >
          Cancel
        </Button>
        {!isRestricted && (
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              textTransform: "none",
              fontWeight: 400,
              width: "114px",
              height: "36px",
              borderRadius: "4px",
              backgroundColor: "#0476E2",
              color: "#FFFFFF",
            }}
          >
            SAVE
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

DomainApproverTargetEcuTypePopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vehiclesData: PropTypes.array,
  ecuTypeData: PropTypes.array,
  exclusions: PropTypes.array,
  inclusions: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  reviewStatus: PropTypes.array,
  readOnly: PropTypes.bool,
};

export default DomainApproverTargetEcuTypePopup;
